import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select as ChakraSelect,
  useToast,
  useColorModeValue,
  Switch,
  Alert,
  AlertIcon,
  Icon,
  Center,
  Spinner,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiUserPlus,
} from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';

/**
 * Deal Party Roles Manager
 * 
 * Admin component to manage roles that can be assigned to contacts on deals.
 * Examples: buyer-agent, title-agent, lender-rep, co-borrower
 * 
 * Configuration:
 * - Role name and label
 * - Allow multiple parties in same role
 * - Which contact types are allowed for this role
 */
export default function DealPartyRolesManager() {
  const toast = useToast();
  const { dealPartyRoles, contactTypes, loading: settingsLoading } = useCrmSettings();
  const [selectedRole, setSelectedRole] = useState(null);
  const [saving, setSaving] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');

  const handleAdd = () => {
    setSelectedRole(null);
    onOpen();
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    onOpen();
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Are you sure you want to delete the "${role.label}" role?`)) {
      return;
    }

    setSaving(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      const updatedRoles = dealPartyRoles.filter(r => r.value !== role.value);

      const result = await updateCrmSettings({
        updates: {
          dealPartyRoles: updatedRoles,
        },
      });

      if (result.data.success) {
        toast({
          title: 'Role deleted',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (settingsLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Deal Party Roles</Heading>
            <Text fontSize="sm" color="gray.600">
              Define roles that can be assigned to contacts on specific deals
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleAdd}
          >
            Add Role
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              About Deal Party Roles
            </Text>
            <Text fontSize="xs">
              These roles are assigned to contacts on a per-deal basis. For example, "Buyer Agent" on Deal 1 
              doesn't automatically carry over to Deal 2 for the same borrower.
            </Text>
          </VStack>
        </Alert>

        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBG}>
              <Tr>
                <Th>Role</Th>
                <Th>Label</Th>
                <Th>Type</Th>
                <Th>Variable</Th>
                <Th>Allow Multiple</Th>
                <Th>Allowed Contact Types</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {dealPartyRoles?.map((role) => (
                <Tr key={role.value} _hover={{ bg: hoverBG }}>
                  <Td fontFamily="mono" fontSize="xs">{role.value}</Td>
                  <Td fontWeight="medium">{role.label}</Td>
                  <Td>
                    <Badge colorScheme={role.internal ? 'teal' : 'purple'} variant="subtle">
                      {role.internal ? 'Internal (user)' : 'External (contact)'}
                    </Badge>
                  </Td>
                  <Td fontFamily="mono" fontSize="xs">
                    {role.variableKey ? `{{${role.variableKey}.*}}` : '—'}
                  </Td>
                  <Td>
                    <Badge colorScheme={role.allowMultiple ? 'green' : 'gray'}>
                      {role.allowMultiple ? 'Yes' : 'No'}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      {role.internal ? (
                        <Text fontSize="xs" color="gray.500">CRM users</Text>
                      ) : (
                        role.contactTypesAllowed?.map(type => {
                          const typeConfig = contactTypes?.find(t => t.value === type);
                          return (
                            <Badge key={type} size="xs" colorScheme={typeConfig?.color || 'gray'}>
                              {typeConfig?.label || type}
                            </Badge>
                          );
                        })
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        icon={<FiEdit2 />}
                        size="xs"
                        variant="ghost"
                        aria-label="Edit"
                        onClick={() => handleEdit(role)}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete"
                        onClick={() => handleDelete(role)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>

      {/* Role Editor Modal */}
      <RoleEditorModal
        isOpen={isOpen}
        onClose={onClose}
        role={selectedRole}
        existingRoles={dealPartyRoles}
        contactTypes={contactTypes}
      />
    </Box>
  );
}

// 'buyer-agent-tc' → 'buyerAgentTc' (default template-variable key)
function toCamelKey(slug) {
  return String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('');
}

function RoleEditorModal({ isOpen, onClose, role, existingRoles, contactTypes }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [selectedContactTypes, setSelectedContactTypes] = useState([]);
  const [icon, setIcon] = useState('FiUserPlus');
  const [isInternal, setIsInternal] = useState(false);
  const [variableKey, setVariableKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (role) {
      setValue(role.value || '');
      setLabel(role.label || '');
      setAllowMultiple(role.allowMultiple || false);
      setIcon(role.icon || 'FiUserPlus');
      setIsInternal(role.internal === true);
      setVariableKey(role.variableKey || toCamelKey(role.value));
      setSelectedContactTypes(
        (role.contactTypesAllowed || []).map(val => ({
          value: val,
          label: contactTypes?.find(t => t.value === val)?.label || val,
        }))
      );
    } else {
      setValue('');
      setLabel('');
      setAllowMultiple(false);
      setIcon('FiUserPlus');
      setIsInternal(false);
      setVariableKey('');
      setSelectedContactTypes([]);
    }
  }, [role, contactTypes, isOpen]);

  const handleSave = async () => {
    if (!value.trim() || !label.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Value and label are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!isInternal && selectedContactTypes.length === 0) {
      toast({
        title: 'Select contact types',
        description: 'At least one contact type must be allowed for an external role',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    const cleanVariableKey = (variableKey || toCamelKey(value)).trim();
    if (!/^[a-z][a-zA-Z0-9]*$/.test(cleanVariableKey)) {
      toast({
        title: 'Invalid variable key',
        description: 'Use camelCase letters/numbers only (e.g. buyersAgentTc)',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (existingRoles?.some(r => r.value !== value.trim() && r.variableKey === cleanVariableKey)) {
      toast({
        title: 'Duplicate variable key',
        description: 'Another role already uses this variable key',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      let updatedRoles = [...(existingRoles || [])];

      const newRole = {
        value: value.trim(),
        label: label.trim(),
        allowMultiple,
        contactTypesAllowed: isInternal ? [] : selectedContactTypes.map(t => t.value),
        icon,
        internal: isInternal,
        variableKey: cleanVariableKey,
      };

      if (role) {
        // Update existing
        const index = updatedRoles.findIndex(r => r.value === role.value);
        if (index > -1) {
          updatedRoles[index] = newRole;
        }
      } else {
        // Add new
        if (updatedRoles.some(r => r.value === value.trim())) {
          toast({
            title: 'Duplicate value',
            description: 'A role with this value already exists',
            status: 'error',
            duration: 3000,
          });
          setIsSaving(false);
          return;
        }
        updatedRoles.push(newRole);
      }

      const result = await updateCrmSettings({
        updates: {
          dealPartyRoles: updatedRoles,
        },
      });

      if (result.data.success) {
        toast({
          title: role ? 'Updated' : 'Created',
          description: `Deal party role ${role ? 'updated' : 'created'} successfully`,
          status: 'success',
          duration: 2000,
        });
        onClose();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {role ? 'Edit' : 'Create'} Deal Party Role
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Value (Unique ID)</FormLabel>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., buyer-agent"
                isDisabled={!!role}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Lowercase, hyphenated. Cannot be changed after creation.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Display Label</FormLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Buyer Agent"
              />
            </FormControl>

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <FormLabel fontSize="sm" mb={0}>Internal Role (CRM User)</FormLabel>
                <Text fontSize="xs" color="gray.600">
                  Internal roles (co-owner, CSC) are filled by CRM users, and assigning one
                  shares the deal with that user. External roles are filled by contacts.
                </Text>
              </Box>
              <Switch
                isChecked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                colorScheme="teal"
                size="lg"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Template Variable Key</FormLabel>
              <Input
                value={variableKey}
                onChange={(e) => setVariableKey(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                placeholder={toCamelKey(value) || 'e.g., buyersAgentTc'}
                fontFamily="mono"
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Powers merge tags like {'{{'}{variableKey || toCamelKey(value) || 'roleKey'}.firstName{'}}'} in campaigns
              </Text>
            </FormControl>

            {!isInternal && (
              <FormControl>
                <FormLabel fontSize="sm">Allowed Contact Types</FormLabel>
                <Select
                  isMulti
                  value={selectedContactTypes}
                  onChange={setSelectedContactTypes}
                  options={contactTypes?.map(type => ({
                    value: type.value,
                    label: type.label,
                  })) || []}
                  placeholder="Select allowed contact types..."
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Only contacts with these types can be assigned to this role
                </Text>
              </FormControl>
            )}

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <FormLabel fontSize="sm" mb={0}>Allow Multiple Parties</FormLabel>
                <Text fontSize="xs" color="gray.600">
                  Can multiple contacts have this role on the same deal?
                </Text>
              </Box>
              <Switch
                isChecked={allowMultiple}
                onChange={(e) => setAllowMultiple(e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </FormControl>

            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="xs">
                Example: "Buyer Agent" might not allow multiple (only one buyer agent per deal), 
                but "Co-Borrower" might allow multiple.
              </Text>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {role ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

